import { View } from "react-native";
import React, { useState, useEffect } from "react";
import { useRouter, useNavigation } from "expo-router";
import { useAuth } from "@components/auth/useAuth";

import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";

import { useFirebase } from "@components/firebase/useFirebase";
import { addContact } from "@/services/firebase/user-functions";
import { getMyGroups } from "@/services/firebase/group-functions";
import { GroupModel } from "common247";
import { ContactEditor } from "@/components/contacts/ContactEditor";
import { navigateBackOrToList } from "@/utils/navigation";

/**
 * Screen for creating a new contact with linear busy indicator during initial fetch and creation.
 * Delegated field validation contracts (Feature 277 & 351):
 * !text.trim() -> setNameError("Full name is required.")
 * !isValidEmail(trimmed) -> setEmailError("Please enter a valid email address (e.g. user@example.com)")
 */
export default function NewContactScreen() {
  const services = useFirebase();
  const { user } = useAuth(true);
  const router = useRouter();
  const navigation = useNavigation();

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [allGroups, setAllGroups] = useState<GroupModel[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!services) return;
      setLoading(true);
      try {
        const groupsRes = await getMyGroups(services, {});
        if (groupsRes.status) {
          setAllGroups(groupsRes.groups || []);
        }
      } catch (error) {
        console.error("Error fetching groups for new contact:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [services]);

  const handleSave = async (formData: any) => {
    if (!services) return;

    setIsSaving(true);
    try {
      const res = await addContact(services, {
        displayName: formData.displayName.trim(),
        email: formData.email ? formData.email.trim() : undefined,
        groupIds: formData.groupIds,
        groups: formData.groups,
        tags: formData.tags,
        photoMediaId: formData.photoMediaId,
        memories: formData.memories,
        media: formData.media,
        places: formData.places,
        anchors: formData.anchors,
      });

      if (res.status) {
        navigateBackOrToList(router, "/(tabs)/my-contacts", navigation);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to create contact.");
      }
    } catch (error) {
      console.error("Error creating contact:", error);
      CustomAlert.alert("Error", "Unexpected error while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ThemedScreen title="Add New Contact" loading={loading || isSaving}>
      <View style={{ flex: 1 }}>
        <ThemedProgressBar loading={loading} />
        {!loading && (
          <ContactEditor
            allGroups={allGroups}
            currentUserId={user?.uid}
            onSave={handleSave}
            onCancel={() => navigateBackOrToList(router, "/(tabs)/my-contacts", navigation)}
            isSaving={isSaving}
            submitTitle="Create Contact"
          />
        )}
      </View>
    </ThemedScreen>
  );
}
